import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Mail, Check, Eye, MousePointerClick, AlertTriangle, TrendingUp } from "lucide-react";
import { API } from "@/lib/admin-constants";

const StatCard = ({ label, value, icon: Icon, color, subtext }) => (
  <Card className="border-cfc-border">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-cfc-text-muted uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-cfc-primary mt-1">{value}</p>
          {subtext && <p className="text-[11px] text-cfc-text-muted mt-0.5">{subtext}</p>}
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const RateBar = ({ label, rate, color }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-xs">
      <span className="text-cfc-text-muted">{label}</span>
      <span className="font-bold text-cfc-primary">{rate}%</span>
    </div>
    <div className="h-2 bg-cfc-cream rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${Math.min(rate, 100)}%` }} />
    </div>
  </div>
);

const EmailAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email/analytics`, { withCredentials: true });
      setData(res.data);
    } catch (error) {
      console.error("Failed to fetch email analytics:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-cfc-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-cfc-text-muted">
        <Mail size={32} className="mx-auto mb-3 opacity-40" />
        <p className="text-sm">Unable to load email analytics.</p>
      </div>
    );
  }

  const { summary, timeline, recent_bounces } = data;
  const hasData = summary.total_sent > 0;

  return (
    <div className="max-w-5xl mx-auto" data-testid="email-analytics">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-cfc-primary">Email Analytics</h1>
        <p className="text-sm text-cfc-text-muted mt-1">
          Real-time delivery and engagement metrics from SendGrid.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard label="Sent" value={summary.total_sent} icon={Mail} color="bg-slate-600" />
        <StatCard label="Delivered" value={summary.delivered} icon={Check} color="bg-green-600" subtext={hasData ? `${summary.delivery_rate}%` : undefined} />
        <StatCard label="Opened" value={summary.opened} icon={Eye} color="bg-blue-600" subtext={hasData ? `${summary.open_rate}%` : undefined} />
        <StatCard label="Clicked" value={summary.clicked} icon={MousePointerClick} color="bg-purple-600" subtext={hasData ? `${summary.click_rate}%` : undefined} />
        <StatCard label="Bounced" value={summary.bounced} icon={AlertTriangle} color="bg-red-500" subtext={hasData ? `${summary.bounce_rate}%` : undefined} />
        <StatCard label="Dropped" value={summary.dropped} icon={AlertTriangle} color="bg-orange-500" />
      </div>

      {/* Rate Bars */}
      {hasData && (
        <Card className="border-cfc-border mb-6">
          <CardContent className="p-5 space-y-3">
            <h3 className="font-heading font-bold text-sm text-cfc-primary mb-3">Engagement Rates</h3>
            <RateBar label="Delivery Rate" rate={summary.delivery_rate} color="bg-green-500" />
            <RateBar label="Open Rate" rate={summary.open_rate} color="bg-blue-500" />
            <RateBar label="Click Rate" rate={summary.click_rate} color="bg-purple-500" />
            <RateBar label="Bounce Rate" rate={summary.bounce_rate} color="bg-red-500" />
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      {timeline.length > 0 && (
        <Card className="border-cfc-border mb-6">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={16} className="text-cfc-primary" />
              <h3 className="font-heading font-bold text-sm text-cfc-primary">30-Day Activity</h3>
            </div>
            <div className="overflow-x-auto">
              <div className="flex items-end gap-[2px] min-w-fit h-32" data-testid="timeline-chart">
                {timeline.map((day) => {
                  const delivered = day.delivered || 0;
                  const opened = day.open || 0;
                  const maxVal = Math.max(...timeline.map(d => (d.delivered || 0) + (d.open || 0) + (d.click || 0)), 1);
                  const total = delivered + opened + (day.click || 0);
                  const height = Math.max((total / maxVal) * 100, 4);
                  return (
                    <div key={day.date} className="flex flex-col items-center gap-0.5 group relative" style={{ minWidth: "14px" }}>
                      <div className="absolute bottom-full mb-1 hidden group-hover:block bg-cfc-primary text-white text-[9px] px-1.5 py-1 rounded whitespace-nowrap z-10">
                        {day.date}<br />
                        {delivered > 0 && `Delivered: ${delivered} `}
                        {opened > 0 && `Opened: ${opened} `}
                        {day.click > 0 && `Clicked: ${day.click}`}
                      </div>
                      <div
                        className="w-2.5 rounded-t bg-gradient-to-t from-cfc-primary to-cfc-accent transition-all"
                        style={{ height: `${height}%` }}
                      />
                      <span className="text-[7px] text-cfc-text-muted -rotate-45 origin-top-left whitespace-nowrap mt-1 hidden md:block">
                        {day.date.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Bounces */}
      {recent_bounces.length > 0 && (
        <Card className="border-red-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={16} className="text-red-500" />
              <h3 className="font-heading font-bold text-sm text-red-700">Recent Bounces & Drops</h3>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recent_bounces.map((b, i) => (
                <div key={`bounce-${b.email}-${b.timestamp}`} className="flex items-start justify-between gap-3 p-2 bg-red-50 rounded text-xs">
                  <div className="min-w-0">
                    <p className="font-medium text-red-800 truncate">{b.email}</p>
                    {b.reason && <p className="text-red-600 mt-0.5 truncate">{b.reason}</p>}
                    {b.bounce_classification && (
                      <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[9px] font-medium">
                        {b.bounce_classification}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-red-400 shrink-0">
                    {new Date(b.timestamp).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!hasData && (
        <div className="text-center py-12 text-cfc-text-muted">
          <Mail size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No email events recorded yet.</p>
          <p className="text-xs mt-1">Events will appear here once SendGrid starts sending webhook data.</p>
        </div>
      )}
    </div>
  );
};

export default EmailAnalytics;
